import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type UserlikeChatbotModuleSpecsFragment } from '../../../../api/fragments/UserlikeChatbotModuleSpecs';
import {
    UpdateUserlikeChatbotModuleDocument,
    type UpdateUserlikeChatbotModuleMutation,
    type UpdateUserlikeChatbotModuleMutationVariables,
} from '../../../../api/mutations/updateUserlikeChatbotModule';
import { type UserlikeChatbotModuleInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type UserlikeChatbotModuleUpdateFormProps = {
    module: UserlikeChatbotModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = UserlikeChatbotModuleInput;

const UserlikeChatbotModuleUpdateForm = ({ module, onComplete }: UserlikeChatbotModuleUpdateFormProps) => {
    const { t } = useTranslation(['userlikeChatbotModuleDetails']);
    const apolloClient = useApolloClient();

    const moduleId = module.id;

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('userlikeChatbotModuleDetails:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateUserlikeChatbotModuleMutation, UpdateUserlikeChatbotModuleMutationVariables>({
                    mutation: UpdateUserlikeChatbotModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('userlikeChatbotModuleDetails:updateDrawer.messages.updateSuccess'),
                key: 'primary',
            });

            onComplete();
        },
        [apolloClient, moduleId, onComplete, t]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName'], module),
        }),
        [module]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateUserlikeChatbotModule"
                    layout="vertical"
                    name="updateUserlikeChatbotModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('userlikeChatbotModuleDetails:updateDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UserlikeChatbotModuleUpdateForm;
